import { useState } from 'react';
import { Link } from 'react-router';
import { MarkPaidDialog } from '../components/MarkPaid';
import { t } from '../i18n';
import { useCashflow } from '../lib/queries';
import { daysBetween, fmtDate, fmtMoney, fmtMonth, todayISO } from '../lib/format';
import { Button, Empty, ErrorState, Ltr, SkeletonRows } from '../components/ui';
import { useOpenDocument } from '../components/Layout';

export function FinancePage() {
  const { data, isLoading, error, refetch } = useCashflow();
  const open = useOpenDocument();
  const [marking, setMarking] = useState<{ id: number; number: string; open: number | null; customerName: string } | null>(null);
  const thisMonth = todayISO().slice(0, 7);
  const max = Math.max(1, ...(data?.months.map((m) => m.expected) ?? [1]));

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.finance.title}</h1>
          <div className="sub">{t.finance.sub}</div>
        </div>
      </div>

      {marking ? <MarkPaidDialog invoices={[marking]} onClose={() => setMarking(null)} /> : null}
      {error ? (
        <div className="panel"><ErrorState error={error} onRetry={() => refetch()} /></div>
      ) : (
        <div className="stack">
          <section className="kpis">
            <div className="kpi">
              <div className="kpi-label">{t.finance.overdue}</div>
              <div className="kpi-value" style={(data?.overdue.amount ?? 0) > 0 ? { color: 'var(--bad)' } : undefined}>{fmtMoney(data?.overdue.amount, { round: true })}</div>
              <div className="kpi-meta">{data?.overdue.count ?? 0} חשבוניות שמועד התשלום שלהן עבר</div>
            </div>
            {data?.months
              .filter((m) => m.month >= thisMonth)
              .slice(0, 3)
              .map((m) => (
                <div className="kpi" key={m.month}>
                  <div className="kpi-label">{m.month === thisMonth ? 'צפוי החודש' : fmtMonth(m.month)}</div>
                  <div className="kpi-value">{fmtMoney(m.open, { round: true })}</div>
                  <div className="kpi-meta">{m.openInvoices} חשבוניות פתוחות · התקבל {fmtMoney(m.received, { round: true })}</div>
                </div>
              ))}
            {data && (data.withoutDueDate.amount ?? 0) > 0 ? (
              <div className="kpi">
                <div className="kpi-label">{t.finance.noDue}</div>
                <div className="kpi-value">{fmtMoney(data.withoutDueDate.amount, { round: true })}</div>
                <div className="kpi-meta">{data.withoutDueDate.count} חשבוניות ללא תאריך</div>
              </div>
            ) : null}
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2>תזרים צפוי לפי חודש פירעון</h2>
              <span className="spacer" />
              <span className="small muted row" style={{ gap: 12 }}>
                <span className="row" style={{ gap: 5 }}><span style={{ width: 10, height: 10, background: 'var(--accent)', borderRadius: 2 }} /> התקבל</span>
                <span className="row" style={{ gap: 5 }}><span style={{ width: 10, height: 10, background: '#9fd3dc', borderRadius: 2 }} /> פתוח</span>
              </span>
            </div>
            <table className="table">
              <thead>
                <tr>
                  <th>חודש</th>
                  <th style={{ width: '40%' }} className="hide-sm" />
                  <th className="num">{t.finance.expected}</th>
                  <th className="num">{t.finance.received}</th>
                  <th className="num">{t.finance.open}</th>
                  <th className="num hide-sm">חשבוניות</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <SkeletonRows cols={6} rows={8} />
                ) : (
                  data?.months.map((m) => (
                    <tr key={m.month} style={m.month === thisMonth ? { background: 'var(--surface-2)' } : undefined}>
                      <td className={m.month === thisMonth ? 'strong' : ''}>
                        {fmtMonth(m.month)}
                        {m.month < thisMonth && m.open > 0 ? <span className="sub" style={{ color: 'var(--bad)' }}>לא נגבה במלואו</span> : null}
                      </td>
                      <td className="hide-sm">
                        {m.expected > 0 ? (
                          <div className="bar-track" style={{ width: `${Math.max(2, (m.expected / max) * 100)}%` }} title={`${fmtMoney(m.received)} / ${fmtMoney(m.expected)}`}>
                            <div className="paid" style={{ width: `${(m.received / m.expected) * 100}%` }} />
                            <div className="open" style={{ width: `${(m.open / m.expected) * 100}%` }} />
                          </div>
                        ) : null}
                      </td>
                      <td className="num">{m.expected ? fmtMoney(m.expected, { round: true }) : <span className="faint">—</span>}</td>
                      <td className="num">{m.received ? fmtMoney(m.received, { round: true }) : <span className="faint">—</span>}</td>
                      <td className="num strong" style={m.month < thisMonth && m.open > 0 ? { color: 'var(--bad)' } : undefined}>{m.open ? fmtMoney(m.open, { round: true }) : <span className="faint">—</span>}</td>
                      <td className="num hide-sm muted">{m.invoices || ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <div className="table-foot">החישוב לפי תאריך הפירעון של כל חשבונית מס — מתנאי התשלום של הלקוח (שוטף + X), או תאריך שנקבע ידנית בחשבונית.</div>
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2>{t.finance.openInvoices}</h2>
              <span className="spacer" />
              {data ? <span className="small muted">{data.openInvoices.length}</span> : null}
            </div>
            {data && data.openInvoices.length === 0 ? (
              <Empty title="אין חשבוניות פתוחות" body="כל החשבוניות שולמו, או שעדיין לא יובאו חשבוניות." />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>לקוח</th>
                      <th>חשבונית</th>
                      <th className="hide-sm">תאריך</th>
                      <th>לתשלום עד</th>
                      <th className="num">{t.docs.cols.total}</th>
                      <th className="num">{t.docs.cols.open}</th>
                      <th className="num">{t.finance.daysLate}</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      <SkeletonRows cols={8} />
                    ) : (
                      data?.openInvoices.map((i) => (
                        <tr key={i.id} className="clickable" onClick={() => open(i.id)}>
                          <td className="primary">
                            <Link to={`/customers/${i.customerId}?tab=invoices`} onClick={(e) => e.stopPropagation()} style={{ color: 'var(--ink)' }}>{i.customerName}</Link>
                          </td>
                          <td><Ltr className="tabular">{i.number}</Ltr></td>
                          <td className="hide-sm tabular">{fmtDate(i.issueDate)}</td>
                          <td className="tabular" style={i.overdue ? { color: 'var(--bad)' } : undefined}>{fmtDate(i.dueDate)}</td>
                          <td className="num">{fmtMoney(i.total)}</td>
                          <td className="num strong">{fmtMoney(i.open)}</td>
                          <td className="num">{i.overdue && i.dueDate ? <span className="badge bad">{daysBetween(i.dueDate)}</span> : <span className="faint">—</span>}</td>
                          <td className="num">
                            <Button
                              size="sm"
                              className="mark-paid-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMarking({ id: i.id, number: i.number, open: i.open, customerName: i.customerName });
                              }}
                            >
                              סמן כשולם
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );
}
